import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { IconWrapper } from '../components/common/IconWrapper';
import { IconType } from 'react-icons';
import { 
  HiRocketLaunch,
  HiUserGroup,
  HiAcademicCap,
  HiGlobeAlt,
  HiArrowRight,
  HiStar,
  HiLightBulb,
  HiHeart,
  HiSparkles,
  HiTrophy,
  HiUsers,
  HiCodeBracket,
  HiChartBar,
  HiCloud,
  HiCog,
  HiEye,
  HiWrenchScrewdriver,
  HiXMark,
  HiDocumentArrowUp,
  HiEnvelope,
  HiPhone,
  HiUser,
  HiServer
} from 'react-icons/hi2';

interface Benefit {
  title: string;
  description: string;
  icon: IconType;
  color: string;
}

interface JobPosition {
  id?: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  icon: IconType;
  color: string;
}

const benefits: Benefit[] = [
  {
    title: 'Growth Opportunities',
    description: 'Accelerate your career with continuous learning and advancement paths.',
    icon: HiRocketLaunch,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    title: 'Collaborative Culture',
    description: 'Work with talented professionals in a supportive environment.',
    icon: HiUserGroup,
    color: 'from-purple-500 to-pink-500'
  },
  {
    title: 'Learning & Development',
    description: 'Access to training, certifications, and skill development programs.',
    icon: HiAcademicCap,
    color: 'from-green-500 to-emerald-500'
  },
  {
    title: 'Global Impact',
    description: 'Work on projects that make a difference worldwide.',
    icon: HiGlobeAlt,
    color: 'from-orange-500 to-red-500'
  },
  {
    title: 'Work-Life Balance',
    description: 'Flexible working arrangements and comprehensive wellness programs.',
    icon: HiHeart,
    color: 'from-pink-500 to-rose-500'
  },
  {
    title: 'Innovation Focus',
    description: 'Work with cutting-edge technologies and innovative solutions.',
    icon: HiSparkles,
    color: 'from-indigo-500 to-blue-500'
  }
];

const jobPositions: JobPosition[] = [
  {
    id: 'full-stack-developer-intern',
    title: 'Full Stack Developer (MERN Stack)',
    department: 'Software Development',
    location: 'Indore',
    type: 'Internship',
    description: 'Join our team as an intern and gain hands-on experience in building modern web applications using the MERN stack (MongoDB, Express.js, React, Node.js). Work on real-world projects, collaborate with experienced developers, and learn industry best practices in full-stack development.',
    requirements: [
      'Basic knowledge of JavaScript, HTML, CSS',
      'Understanding of React.js fundamentals',
      'Familiarity with Node.js and Express.js',
      'Knowledge of MongoDB or any database',
      'Strong problem-solving skills',
      'Good communication skills',
      'Willingness to learn and adapt'
    ],
    icon: HiCodeBracket,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'full-stack-developer',
    title: 'Full Stack Developer (MERN Stack)',
    department: 'Software Development',
    location: 'Indore',
    type: 'Full-time',
    description: 'We are seeking an experienced Full Stack Developer with 2-5 years of experience in MERN stack development. You will be responsible for designing, developing, and maintaining scalable web applications. Work on cutting-edge projects, collaborate with cross-functional teams, and contribute to the entire software development lifecycle.',
    requirements: [
      '2-5 years of experience in MERN stack development',
      'Strong proficiency in JavaScript, React.js, Node.js, and Express.js',
      'Experience with MongoDB and database design',
      'Knowledge of RESTful APIs and GraphQL',
      'Familiarity with Git version control',
      'Experience with cloud platforms (AWS/Azure)',
      'Understanding of Agile/Scrum methodologies',
      'Strong debugging and problem-solving skills'
    ],
    icon: HiCodeBracket,
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'azure-data-engineer',
    title: 'Azure Data Engineer',
    department: 'Data Engineering',
    location: 'Indore',
    type: 'Full-time',
    description: 'We are looking for an Azure Data Engineer with 2-5 years of experience to design, build, and maintain data pipelines on Microsoft Azure. You will work with Azure Data Factory, Azure Databricks, Azure Synapse Analytics, and other Azure data services to create scalable data solutions for our clients.',
    requirements: [
      '2-5 years of experience in data engineering',
      'Strong experience with Azure Data Factory, Azure Databricks',
      'Proficiency in SQL and Python',
      'Experience with Azure Synapse Analytics, Azure Data Lake',
      'Knowledge of ETL/ELT processes and data modeling',
      'Understanding of data warehousing concepts',
      'Experience with Azure DevOps and CI/CD pipelines',
      'Strong analytical and problem-solving skills'
    ],
    icon: HiCloud,
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'sql-developer',
    title: 'SQL Developer',
    department: 'Database Development',
    location: 'Indore',
    type: 'Full-time',
    description: 'Join our team as a SQL Developer with 2-5 years of experience. You will be responsible for designing, developing, and optimizing database solutions. Write complex SQL queries, create stored procedures, optimize database performance, and ensure data integrity across various database systems.',
    requirements: [
      '2-5 years of experience as SQL Developer',
      'Expert-level knowledge of SQL and T-SQL',
      'Experience with SQL Server, MySQL, or PostgreSQL',
      'Strong understanding of database design and normalization',
      'Experience writing stored procedures, functions, and triggers',
      'Knowledge of database performance tuning and optimization',
      'Understanding of data warehousing concepts',
      'Experience with ETL processes and data integration'
    ],
    icon: HiServer,
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'power-bi-expert',
    title: 'Power BI Expert',
    department: 'Business Intelligence',
    location: 'Indore',
    type: 'Full-time',
    description: 'We are seeking a Power BI Expert with 2-5 years of experience to create interactive dashboards, reports, and data visualizations. You will work closely with business stakeholders to understand requirements, design data models, and deliver actionable insights through Power BI solutions.',
    requirements: [
      '2-5 years of experience with Power BI',
      'Expert knowledge of Power BI Desktop, Power BI Service',
      'Strong SQL skills for data extraction and transformation',
      'Experience with DAX (Data Analysis Expressions)',
      'Knowledge of Power Query and M language',
      'Understanding of data modeling and relationships',
      'Experience creating interactive dashboards and reports',
      'Strong analytical and visualization skills'
    ],
    icon: HiChartBar,
    color: 'from-orange-500 to-amber-500'
  }
];

const sectionTitleClass = "text-3xl md:text-4xl font-bold text-slate-900 dark:text-white";

const heroImage = 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=60';

interface ApplicationFormData {
  name: string;
  email: string;
  phone: string;
  position: string;
  coverLetter: string;
  resume: File | null;
}

const ApplicationFormModal: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  position: string;
}> = ({ isOpen, onClose, position }) => {
  const [formData, setFormData] = useState<ApplicationFormData>({
    name: '',
    email: '',
    phone: '',
    position: position,
    coverLetter: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, resume: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    // Reset form after 2 seconds
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: position,
        coverLetter: '',
        resume: null
      });
      setSubmitSuccess(false);
      onClose();
    }, 2000);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: position,
        coverLetter: '',
        resume: null
      });
      setSubmitSuccess(false);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-slate-950 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between rounded-t-3xl">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Apply for Position</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{position}</p>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors disabled:opacity-50"
                >
                  <IconWrapper icon={HiXMark} className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {submitSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="inline-flex p-4 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
                      <IconWrapper icon={HiStar} className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Application Submitted!</h3>
                    <p className="text-slate-600 dark:text-slate-400">We'll review your application and get back to you soon.</p>
                  </motion.div>
                ) : (
                  <>
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IconWrapper icon={HiUser} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IconWrapper icon={HiEnvelope} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="john.doe@example.com"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IconWrapper icon={HiPhone} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* Resume Upload */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Resume/CV <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                          className="hidden"
                          id="resume-upload"
                        />
                        <label
                          htmlFor="resume-upload"
                          className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
                          <IconWrapper icon={HiDocumentArrowUp} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          <div className="flex-1">
                            {formData.resume ? (
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">{formData.resume.name}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{(formData.resume.size / 1024).toFixed(2)} KB</p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">Click to upload resume</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">PDF, DOC, or DOCX (Max 5MB)</p>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Cover Letter */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Cover Letter
                      </label>
                      <textarea
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleInputChange}
                        rows={5}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Tell us why you're interested in this position..."
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1 px-6 py-3 border border-slate-200 dark:border-slate-700 rounded-full font-semibold text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Application
                            <IconWrapper icon={HiArrowRight} className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export const CareersPage: React.FC = () => {
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleApplyClick = (position: string) => {
    setSelectedPosition(position);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      {/* Hero Banner Section */}
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Careers at FirstLogic"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <IconWrapper icon={HiStar} className="w-5 h-5 text-blue-400 mr-2" />
              <span className="text-sm font-medium text-white">
            Join Our Team
              </span>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
                Build Your Career
            </motion.h1>

            <motion.p
              className="text-xl text-white/85 mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Shape the Future of Technology with FirstLogic
            </motion.p>

            <motion.p
              className="text-lg text-white/80 mb-8 leading-relaxed max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Join a team of passionate professionals building cutting-edge data engineering, 
              analytics, and cloud solutions that transform businesses worldwide.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <motion.button
                className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-full shadow-sm hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const element = document.getElementById('open-positions');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                  View Open Positions
                <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 inline" />
              </motion.button>

              <motion.button
                className="flex items-center px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-full hover:bg-white/10 backdrop-blur-sm transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <IconWrapper icon={HiUsers} className="w-5 h-5 mr-2" />
                Learn About Us
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Open Positions Section */}
      <section id="open-positions" className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className={`${sectionTitleClass} mb-4`}>Open Positions</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Explore exciting career opportunities in data engineering, analytics, cloud, and AI/ML
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {jobPositions.map((job, index) => {
              const jobId = job.id || job.title.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '');

              return (
                <Link
                  key={index}
                  to={`/careers/${jobId}`}
                  className="block"
                >
                  <motion.div
                    className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col h-full cursor-pointer group"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <p className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{job.department}</p>
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{job.title}</h3>
                      </div>
                      <div className="inline-flex p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 group-hover:scale-110 transition-transform">
                        <IconWrapper icon={job.icon} className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-4 text-sm">
                      <span className="px-3 py-1 border border-slate-200 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-300">
                        {job.location}
                      </span>
                      <span className="px-3 py-1 border border-slate-200 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-300">
                        {job.type}
                      </span>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3">
                      {job.description}
                    </p>
                    <div className="mt-auto pt-4 border-t border-slate-200 dark:border-slate-800">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 group-hover:underline">
                          View Details
                        </span>
                        <IconWrapper icon={HiArrowRight} className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Culture & Values Section */}
      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className={`${sectionTitleClass} mb-4`}>Our Culture & Values</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              At FirstLogic, we foster a culture of innovation, collaboration, and continuous learning
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Innovation',
                description: 'We encourage creative thinking and embrace new technologies to solve complex challenges.',
                icon: HiLightBulb,
                color: 'from-yellow-500 to-orange-500'
              },
              {
                title: 'Collaboration',
                description: 'We believe in the power of teamwork and diverse perspectives to achieve great results.',
                icon: HiUserGroup,
                color: 'from-blue-500 to-cyan-500'
              },
              {
                title: 'Excellence',
                description: 'We strive for the highest quality in everything we do, from code to customer service.',
                icon: HiTrophy,
                color: 'from-purple-500 to-pink-500'
              },
              {
                title: 'Growth',
                description: 'We invest in our people through continuous learning and career development opportunities.',
                icon: HiAcademicCap,
                color: 'from-green-500 to-emerald-500'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                className="text-center p-6 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 mb-4">
                  <IconWrapper icon={value.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
        </div>
      </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-slate-200 dark:border-slate-800 rounded-3xl p-10 text-center bg-white dark:bg-slate-950 shadow-sm">
            <h2 className={`${sectionTitleClass} mb-4`}>Ready to Join Our Team?</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
              Be part of a team that's shaping the future of technology and driving digital transformation for enterprises worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors">
                  View All Positions
              </button>
              <button className="px-8 py-4 border border-slate-300 dark:border-slate-700 rounded-full font-semibold text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors">
                  Contact HR Team
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Modal */}
      <ApplicationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        position={selectedPosition}
      />
    </div>
  );
};